import {
  useState,
  createContext,
  useContext,
  useMemo,
  ReactElement,
  useCallback,
  useEffect,
  ReactNode,
} from 'react';
import {AxiosError} from 'axios';
import {useNavigate, useUrl} from '@shopify/hydrogen';
import {
  useQuery as useBackendQuery,
  useMutation,
  UseMutateFunction,
} from 'react-query';

import {
  ValidationErrorType,
  GenericErrorType,
  UserWithTokenPairResponseType,
  MemberType,
  AuthUserType,
  MemberProductType,
  SocialAuthUserWithTokenPairResponseType,
} from '../types';
import {useModalToggle, useNextParam} from '../hooks';
import {useLocalStorage} from '../hooks/useLocalStorage.client';

import {
  authRefresh,
  loginWithEmail,
  signupWithEmailStep1,
  signupStep2,
  SignupWithEmailStepOnePayloadType,
  SignupStepTwoPayloadType,
  LoginWithEmailPayloadType,
  getListOfMyProducts,
  loginWithMagicToken,
  SocialAuthPayloadType,
  socialAuth,
} from '../backendApi';
import {useGlobalState} from '../hooks/useGlobalState.client';
import {wisepops} from '../utils/wisepopups.client';

type UserSignupStep = 1 | 2 | null;

interface OnLoginSuccessActionType {
  next?: string;
  onSuccess?: () => void;
}

interface AuthServiceContextProps {
  signupStep: UserSignupStep;
  handleSignupWithEmailStep1: UseMutateFunction<
    UserWithTokenPairResponseType,
    AxiosError,
    SignupWithEmailStepOnePayloadType
  >;
  handleSignupStep2: UseMutateFunction<
    MemberType,
    AxiosError,
    SignupStepTwoPayloadType
  >;
  handleLoginWithEmail: UseMutateFunction<
    UserWithTokenPairResponseType,
    AxiosError,
    LoginWithEmailPayloadType
  >;
  handleSocialAuth: UseMutateFunction<
    SocialAuthUserWithTokenPairResponseType,
    AxiosError,
    SocialAuthPayloadType
  >;
  logout: () => void;
  ready: boolean;
  isLoading: boolean;
  authUser: AuthUserType;
  error: string | Record<string, string>;
  isLoginModalOpen: boolean;
  openLoginModal: (data?: OnLoginSuccessActionType) => void;
  closeLoginModal: () => void;
  memberProducts: Array<MemberProductType>;
  findMemberProduct: (shopifyId: string) => MemberProductType | null;
  checkLoginRequired: (callback: () => void) => void;
}

const AuthServiceContext = createContext<AuthServiceContextProps>(null);

interface AuthServiceProviderProps {
  children: ReactNode | ReactNode[];
  fallback: ReactNode;
}

/*
 * Common interface for all auth related actions.
 * Backend server returns a token pair upon successful authentication.
 * - access: stored as global application state
 * - refresh: stored in local storage (opted for local storage - cookie is probably more secure)
 */
export function AuthServiceProvider({
  children,
}: AuthServiceProviderProps): ReactElement {
  const [_accessToken, setAccessToken] = useGlobalState('accessToken');
  const [ready, setReady] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => setIsLoaded(true), []);

  const {
    getItem: getRefreshToken,
    setItem: setRefreshToken,
    removeItem: removeRefreshToken,
  } = useLocalStorage('refreshToken');

  const {data: authRefreshData, isFetched} = useBackendQuery(
    ['authRefresh'],
    async () => {
      if (!_accessToken) {
        const refreshToken = getRefreshToken();
        if (refreshToken) {
          try {
            const refreshTokenResponse = await authRefresh({
              refresh: refreshToken,
            });
            const {access, refresh} = refreshTokenResponse;
            setAccessToken(access);
            setRefreshToken(refresh);
            return refreshTokenResponse;
          } catch (e) {
            removeRefreshToken();
          }
        }
      }
    },
    {
      enabled: isLoaded,
    },
  );

  useEffect(() => {
    isFetched && setReady(true);
  }, [isFetched]);

  useEffect(() => {
    setAuthUser((value) => value ?? authRefreshData?.user);
  }, [authRefreshData?.user]);

  const [authUser, setAuthUser] = useState<AuthUserType>(authRefreshData?.user);
  const [error, setError] = useState<string | Record<string, string>>();
  const [signupStep, setSignupStep] = useState<UserSignupStep>(1);
  const navigate = useNavigate();

  const handleAuthError = useCallback((error: AxiosError) => {
    if (error.response.status === 400) {
      const {
        response: {data},
      } = error as AxiosError<ValidationErrorType>;
      setAuthUser(null);
      setError(data.errors);
    } else {
      const {
        response: {data},
      } = error as AxiosError<GenericErrorType>;
      setAuthUser(null);
      setError(data.detail);
    }
  }, []);

  // --- Signup ---
  const {
    mutate: handleSignupWithEmailStep1,
    isLoading: isSignupWithEmailStep1Loading,
  } = useMutation(signupWithEmailStep1, {
    onSuccess: (data) => {
      const {user, access, refresh} = data;
      setAccessToken(access);
      setRefreshToken(refresh);
      setAuthUser(user);
      // setSignupStep(2);
      setError('');
      navigate('/my/profile');
    },
    onError: (error: AxiosError) => handleAuthError(error),
  });

  const {mutate: handleSignupStep2} = useMutation(signupStep2, {
    onSuccess: () => {
      navigate('/my/profile');
    },
    onError: (error: AxiosError) => handleAuthError(error),
  });

  const {searchParams, href} = useUrl();

  const {
    modalData: loginModalData,
    openModal: openLoginModal,
    closeModal: closeLoginModal,
    isOpen: isLoginModalOpen,
  } = useModalToggle<OnLoginSuccessActionType>('login');

  const next = useNextParam(loginModalData?.next || '/my/profile');
  const handleLoginSuccess = useCallback(
    (
      data:
        | UserWithTokenPairResponseType
        | SocialAuthUserWithTokenPairResponseType,
    ) => {
      closeLoginModal();
      const {user, access, refresh} = data;
      setAccessToken(access);
      setRefreshToken(refresh);
      setAuthUser(user);
      setSignupStep(null);
      navigate(next, {reloadDocument: true});
      loginModalData?.onSuccess?.();
    },
    [
      closeLoginModal,
      setAccessToken,
      setRefreshToken,
      navigate,
      next,
      loginModalData,
    ],
  );

  // --- Login/Signup - Social Auth ---
  const {mutate: handleSocialAuth, isLoading: isSocialAuthLoading} =
    useMutation(socialAuth, {
      onSuccess: (data) => handleLoginSuccess(data),
      onError: (error: AxiosError) => handleAuthError(error),
    });

  // --- Login - Email ---
  const {mutate: handleLoginWithEmail, isLoading: isLoginWithEmailLoading} =
    useMutation(loginWithEmail, {
      onSuccess: (data) => handleLoginSuccess(data),
      onError: (error: AxiosError) => handleAuthError(error),
    });

  const isLoginRequired = useMemo(() => !authUser, [authUser]);

  const checkLoginRequired = useCallback(
    (callback: () => void): void => {
      if (isLoginRequired)
        return openLoginModal({next: href, onSuccess: callback});
      callback();
    },
    [isLoginRequired, openLoginModal, href],
  );

  // --- Login - Magic Token ---
  const {mutate: handleLoginWithMagicToken} = useMutation(loginWithMagicToken, {
    onSuccess: (data) => {
      const {user, access, refresh} = data;
      setAccessToken(access);
      setRefreshToken(refresh);
      setAuthUser(user);
      navigate('/my/profile');
    },
    onError: (error: AxiosError) => handleAuthError(error),
  });

  useEffect(() => {
    const magicToken = searchParams.get('magic_token');
    if (magicToken) {
      handleLoginWithMagicToken({magic_token: magicToken});
    }
  }, [searchParams, handleLoginWithMagicToken]);

  // --- Logout ---
  const logout = useCallback(() => {
    setAccessToken(null);
    removeRefreshToken();
    setAuthUser(null);
    navigate('/');
  }, [setAccessToken, setAuthUser, navigate, removeRefreshToken]);

  const isLoading = useMemo(
    () =>
      isSignupWithEmailStep1Loading ||
      isLoginWithEmailLoading ||
      isSocialAuthLoading,
    [
      isSignupWithEmailStep1Loading,
      isLoginWithEmailLoading,
      isSocialAuthLoading,
    ],
  );

  // --- Member Products ---
  const [memberProducts, setMemberProducts] =
    useState<Array<MemberProductType>>();

  const {data: myMemberProducts} = useBackendQuery(
    'allMyMemberProducts',
    () => getListOfMyProducts(),
    {
      retry: 1,
      refetchOnWindowFocus: false,
      enabled: !!authUser,
    },
  );

  useEffect(
    () =>
      wisepops('properties', {
        ...authUser?.member?.tags, // Include tags if any
        isAuthed: Boolean(authUser),
        isRewardsMember: Boolean(authUser?.member?.is_rewards_member),
      }),
    [authUser],
  );

  useEffect(
    () => setMemberProducts(authUser ? myMemberProducts : []),
    [authUser, myMemberProducts],
  );

  const findMemberProduct = useCallback(
    (shopifyId: string): MemberProductType => {
      if (memberProducts && shopifyId) {
        // NOTE: shopifyId = gid://shopify/Product/<id>
        return (
          memberProducts.find(
            (memberProduct) =>
              memberProduct.product.shopify_resource_id === shopifyId,
          ) || null
        );
      }
      return null;
    },
    [memberProducts],
  );

  return (
    <AuthServiceContext.Provider
      value={{
        signupStep,
        ready,
        handleSignupWithEmailStep1,
        handleSignupStep2,
        handleLoginWithEmail,
        handleSocialAuth,
        logout,
        isLoading,
        authUser,
        error,
        isLoginModalOpen,
        openLoginModal,
        closeLoginModal,
        memberProducts,
        findMemberProduct,
        checkLoginRequired,
      }}
    >
      {children}
    </AuthServiceContext.Provider>
  );
}

export function useAuthService(): AuthServiceContextProps {
  const context = useContext(AuthServiceContext);
  if (context === undefined) {
    throw new Error('useAuthService must be used within a AuthServiceContext');
  }

  return context;
}

interface AuthReadySuspense {
  children: ReactNode | ReactNode[];
  fallback: ReactNode;
}

export function AuthReadySuspense({
  children,
  fallback,
}: AuthReadySuspense): ReactElement {
  const {ready} = useAuthService();

  return <>{ready ? children : fallback}</>;
}
